// VIRO wordmark + isotype. Single source of truth for the visual mark.
// See DESIGN.md §12.

type LogoProps = {
  height?: number;
  color?: string;
  // Set to true when an ancestor (e.g. a link or button) already exposes
  // an accessible name for this mark, so the SVG shouldn't add its own
  // (avoids a redundant/confusing screen reader announcement).
  decorative?: boolean;
};

type LogoMarkProps = {
  size?: number;
  color?: string;
  decorative?: boolean;
};

const WORDMARK_ASPECT_RATIO = 3.5524; // 1627 / 458

// Horizontal wordmark ("VIRO"). Width is always derived from height so the
// mark never gets distorted by an independent width.
export function Logo({ height = 32, color = "currentColor", decorative = false }: LogoProps) {
  const width = height * WORDMARK_ASPECT_RATIO;
  const a11yProps = decorative
    ? { "aria-hidden": "true" as const }
    : { role: "img" as const, "aria-label": "VIRO" };

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 1627 458"
      xmlns="http://www.w3.org/2000/svg"
      // Guards against ancestor flex containers (e.g. `.brand-lockup`,
      // `.login-brand`) shrinking only the main-axis size and leaving the
      // cross-axis height untouched, which distorts the aspect ratio. The
      // mark must defend itself here since call sites keep forgetting it.
      style={{ flexShrink: 0 }}
      {...a11yProps}
    >
      {/* The original mark is a vertical composition traced with potrace; this
          double transform rotates it 90° into the horizontal wordmark without
          redrawing any path. Do not simplify or remove it. */}
      <g transform="translate(1627,0) rotate(90)" fill={color}>
        <g transform="translate(0,1627) scale(0.1,-0.1)">
          <path d="M1985 16254 c-380 -49 -762 -206 -1081 -445 -483 -361 -815 -937 -889 -1539 -17 -144 -20 -388 -4 -528 112 -1031 904 -1860 1929 -2017 489 -74 1032 23 1453 262 919 520 1363 1559 1102 2579 -233 910 -1025 1591 -1967 1693 -132 15 -409 12 -543 -5z m575 -799 c450 -77 862 -393 1070 -820 85 -174 125 -328 140 -530 25 -354 -55 -665 -241 -946 -84 -127 -291 -334 -418 -418 -252 -167 -507 -242 -821 -243 -226 0 -405 33 -580 106 -462 195 -810 626 -896 1111 -24 136 -24 413 0 544 73 400 310 751 660 983 197 130 416 206 671 232 78 8 321 -3 415 -19z" />
          <path d="M1125 10744 c-524 -94 -968 -540 -1079 -1084 -34 -165 -37 -293 -34 -1315 l3 -1010 398 -3 397 -2 0 1044 c0 720 3 1062 11 1098 32 154 157 316 299 387 169 84 361 77 511 -20 124 -79 237 -249 259 -389 6 -41 10 -441 10 -1092 l0 -1028 400 0 400 0 0 903 0 902 828 320 c455 175 878 339 940 363 l112 44 0 450 c0 356 -3 449 -12 445 -115 -44 -1996 -770 -2004 -774 -6 -2 -33 33 -59 79 -181 317 -478 560 -795 651 -163 47 -419 61 -585 31z" />
          <path d="M0 5675 l0 -395 2280 0 c1254 0 2280 2 2280 5 0 3 -131 181 -291 395 l-291 390 -1989 0 -1989 0 0 -395z" />
          <path d="M0 4053 l0 -458 1385 -669 c762 -368 1385 -672 1385 -676 0 -4 -622 -306 -1382 -672 l-1383 -665 -3 -458 c-2 -384 0 -456 12 -452 23 9 4541 2240 4548 2246 5 5 -2581 1287 -4504 2233 l-58 28 0 -457z" />
        </g>
      </g>
    </svg>
  );
}

// Isotype (the "O" alone). Used for square spaces such as the favicon.
export function LogoMark({ size = 32, color = "currentColor", decorative = false }: LogoMarkProps) {
  const a11yProps = decorative
    ? { "aria-hidden": "true" as const }
    : { role: "img" as const, "aria-label": "VIRO" };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 457 457"
      xmlns="http://www.w3.org/2000/svg"
      // Same flex-shrink guard as the wordmark above: a square isotype can
      // still be squashed on one axis inside a flex row/column.
      style={{ flexShrink: 0 }}
      {...a11yProps}
    >
      <g transform="translate(0,1627) scale(0.1,-0.1)" fill={color}>
        <path d="M1985 16254 c-380 -49 -762 -206 -1081 -445 -483 -361 -815 -937 -889 -1539 -17 -144 -20 -388 -4 -528 112 -1031 904 -1860 1929 -2017 489 -74 1032 23 1453 262 919 520 1363 1559 1102 2579 -233 910 -1025 1591 -1967 1693 -132 15 -409 12 -543 -5z m575 -799 c450 -77 862 -393 1070 -820 85 -174 125 -328 140 -530 25 -354 -55 -665 -241 -946 -84 -127 -291 -334 -418 -418 -252 -167 -507 -242 -821 -243 -226 0 -405 33 -580 106 -462 195 -810 626 -896 1111 -24 136 -24 413 0 544 73 400 310 751 660 983 197 130 416 206 671 232 78 8 321 -3 415 -19z" />
      </g>
    </svg>
  );
}
